import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { WalletCards } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty } from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import { formatCurrency, formatDate } from '@/lib/utils'
import { financeApi } from '@/services/finance'
import { getOrgWalletLabel, orgWalletApi } from '@/services/org-wallet'
import type { JobInvoicePayment } from '@/types'

type JobPaymentsPanelProps = {
  jobId: string
  serviceInstanceId?: string | null
}

const toAmount = (value?: number | string | null) => {
  const parsed = Number(value ?? 0)
  return Number.isFinite(parsed) ? parsed : 0
}

const formatDateTimeInputValue = (date = new Date()) => {
  const offsetMs = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16)
}

const getPaymentWalletLabel = (payment: JobInvoicePayment) => {
  const method = payment.payment_method
  if (method && typeof method === 'object') {
    return method.label || method.wallet_name || method.wallet_variant_name || `Wallet ${method.id ?? ''}`.trim()
  }
  return payment.payment_source_label || 'Wallet'
}

const getPaymentStageLabel = (stage?: string) => {
  if (stage === 'invoice_payment') {
    return 'Invoice payment'
  }
  return 'Advance'
}

export function JobPaymentsPanel({ jobId, serviceInstanceId }: JobPaymentsPanelProps) {
  const queryClient = useQueryClient()
  const [amount, setAmount] = useState('')
  const [walletId, setWalletId] = useState('')
  const [paymentDate, setPaymentDate] = useState(formatDateTimeInputValue)
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')

  const paymentsQuery = useQuery({
    queryKey: ['job-payments', jobId, serviceInstanceId ?? 'none'],
    queryFn: () => financeApi.getJobPayments({
      job_id: jobId,
      page_size: 200,
      service_instance_id: serviceInstanceId ?? undefined,
    }),
    enabled: Boolean(jobId),
  })

  const invoicePreviewQuery = useQuery({
    queryKey: ['invoice-preview', jobId, serviceInstanceId ?? 'none'],
    queryFn: () => financeApi.getInvoicePreview(jobId, serviceInstanceId ?? undefined),
    enabled: Boolean(jobId),
  })

  const walletsQuery = useQuery({
    queryKey: ['job-payment-wallets', serviceInstanceId ?? 'none'],
    queryFn: () => orgWalletApi.listWallets(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const payments = paymentsQuery.data?.results ?? []
  const invoicePreview = invoicePreviewQuery.data

  const advanceFromPayments = useMemo(
    () => payments
      .filter((payment) => (payment.payment_stage ?? 'advance') === 'advance' && (payment.paid_status ?? 'paid') === 'paid')
      .reduce((sum, payment) => sum + toAmount(payment.amount), 0),
    [payments],
  )
  const totalInvoicePaid = useMemo(
    () => payments
      .filter((payment) => payment.payment_stage === 'invoice_payment' && (payment.paid_status ?? 'paid') === 'paid')
      .reduce((sum, payment) => sum + toAmount(payment.amount), 0),
    [payments],
  )

  const totalAdvancePaid = toAmount(invoicePreview?.advance_payment) || advanceFromPayments
  const totalPaid = totalAdvancePaid + totalInvoicePaid
  const taxAdded = toAmount(invoicePreview?.tax_amount)
  const taxIncluded = toAmount(invoicePreview?.included_tax_amount)
  const invoiceTotal = toAmount(invoicePreview?.grand_total)
  const paymentDue = toAmount(invoicePreview?.payment_due)
  const taxBreakdown = invoicePreview?.tax_breakdown ?? []
  const addedTaxRows = (invoicePreview?.tax_rows ?? []).length > 0
    ? (invoicePreview?.tax_rows ?? [])
    : taxBreakdown
        .filter((row) => row.included !== true && row.included !== 1 && row.included !== '1' && row.included !== 'true')
        .map((row) => ({
          label: row.name ?? row.tax_label_name ?? 'Tax',
          amount: row.tax_amount ?? row.value,
          rate: row.value,
        }))

  const createPaymentMutation = useMutation({
    mutationFn: () => {
      const parsedAmount = Number(amount)
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        throw new Error('Enter a valid payment amount.')
      }
      if (!walletId) {
        throw new Error('Select the wallet that received this payment.')
      }

      return financeApi.createJobPayment({
        job: jobId,
        amount: parsedAmount,
        payment_method_id: walletId,
        payment_stage: 'advance',
        paid_status: 'paid',
        payment_date: paymentDate ? new Date(paymentDate).toISOString() : undefined,
        reference: reference.trim(),
        notes: notes.trim(),
      })
    },
    onSuccess: () => {
      toast.success('Payment recorded')
      setAmount('')
      setReference('')
      setNotes('')
      setPaymentDate(formatDateTimeInputValue())
      queryClient.invalidateQueries({ queryKey: ['job-payments', jobId] })
      queryClient.invalidateQueries({ queryKey: ['invoice-preview', jobId] })
      queryClient.invalidateQueries({ queryKey: ['job-invoices', jobId] })
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const summaryLoading = paymentsQuery.isLoading || invoicePreviewQuery.isLoading

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Advance paid</CardTitle>
            <CardDescription>Legacy advance plus recorded advance payments.</CardDescription>
          </CardHeader>
          <CardContent>
            {summaryLoading ? <Skeleton className="h-8 w-28" /> : (
              <p className="text-2xl font-semibold">{formatCurrency(totalAdvancePaid)}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Invoice payments</CardTitle>
            <CardDescription>Payments recorded against issued invoices.</CardDescription>
          </CardHeader>
          <CardContent>
            {summaryLoading ? <Skeleton className="h-8 w-28" /> : (
              <p className="text-2xl font-semibold">{formatCurrency(totalInvoicePaid)}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Total received</CardTitle>
            <CardDescription>Advance and invoice payments combined.</CardDescription>
          </CardHeader>
          <CardContent>
            {summaryLoading ? <Skeleton className="h-8 w-28" /> : (
              <p className="text-2xl font-semibold">{formatCurrency(totalPaid)}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Payment due</CardTitle>
            <CardDescription>Outstanding after advance on invoice total.</CardDescription>
          </CardHeader>
          <CardContent>
            {summaryLoading ? <Skeleton className="h-8 w-28" /> : (
              <p className="text-2xl font-semibold">{invoiceTotal > 0 ? formatCurrency(paymentDue) : '-'}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Invoice totals & taxes</CardTitle>
          <CardDescription>Live invoice preview including tax breakdown for this job.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {invoicePreviewQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : !invoicePreview ? (
            <p className="text-sm text-muted-foreground">Invoice preview is not available for this job yet.</p>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <SummaryMetric label="Customer payable" value={formatCurrency(toAmount(invoicePreview.customer_payable))} />
                <SummaryMetric label="Labour charges" value={formatCurrency(toAmount(invoicePreview.total_pay))} />
                <SummaryMetric label="Billable expenses" value={formatCurrency(toAmount(invoicePreview.total_expenses))} />
                <SummaryMetric label="Inventory particulars" value={formatCurrency(toAmount(invoicePreview.total_particulars))} />
                <SummaryMetric label="Tax added" value={formatCurrency(taxAdded)} />
                <SummaryMetric label="Tax included" value={formatCurrency(taxIncluded)} />
                <SummaryMetric label="Invoice total" value={formatCurrency(invoiceTotal)} emphasized />
                <SummaryMetric label="Advance applied" value={formatCurrency(totalAdvancePaid)} />
                <SummaryMetric label="Balance due" value={formatCurrency(paymentDue)} emphasized />
              </div>

              {invoicePreview.included_tax_note ? (
                <p className="rounded-lg border border-border/70 bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                  {invoicePreview.included_tax_note}
                </p>
              ) : null}

              {addedTaxRows.length > 0 ? (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">Tax breakdown</p>
                    <div className="overflow-hidden rounded-xl border border-border/70">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/35 text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
                          <tr>
                            <th className="px-4 py-3">Tax</th>
                            <th className="px-4 py-3">Rate</th>
                            <th className="px-4 py-3 text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {addedTaxRows.map((row, index) => (
                            <tr key={`${row.label ?? 'tax'}-${index}`} className="border-t border-border/60">
                              <td className="px-4 py-3">{row.label ?? 'Tax'}</td>
                              <td className="px-4 py-3 text-muted-foreground">
                                {row.rate != null && String(row.rate).trim() ? `${row.rate}%` : '-'}
                              </td>
                              <td className="px-4 py-3 text-right font-medium tabular-nums">
                                {formatCurrency(toAmount(row.amount))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Record advance payment</CardTitle>
          <CardDescription>Choose the wallet that received the payment. Cash, bank, UPI, card, and gateways should be configured as wallets.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="job-payment-amount">Amount</Label>
            <Input
              id="job-payment-amount"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="space-y-2">
            <Label>Received in wallet</Label>
            <Select value={walletId} onValueChange={setWalletId} disabled={!serviceInstanceId || walletsQuery.isLoading}>
              <SelectTrigger>
                <SelectValue placeholder={walletsQuery.isLoading ? 'Loading wallets...' : 'Select wallet'} />
              </SelectTrigger>
              <SelectContent>
                {(walletsQuery.data ?? []).map((wallet) => (
                  <SelectItem key={wallet.id} value={String(wallet.id)}>
                    {getOrgWalletLabel(wallet)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!walletsQuery.isLoading && serviceInstanceId && (walletsQuery.data ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No wallets found for this service instance.</p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="job-payment-date">Payment date</Label>
            <Input
              id="job-payment-date"
              type="datetime-local"
              value={paymentDate}
              onChange={(event) => setPaymentDate(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="job-payment-reference">Reference</Label>
            <Input
              id="job-payment-reference"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              placeholder="Receipt, UTR, gateway reference"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="job-payment-notes">Notes</Label>
            <Textarea
              id="job-payment-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional payment notes"
              rows={2}
            />
          </div>
          <div className="md:col-span-2">
            <Button
              type="button"
              disabled={createPaymentMutation.isPending || !walletId || !amount}
              onClick={() => createPaymentMutation.mutate()}
            >
              Record payment
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Payment history</CardTitle>
          <CardDescription>All customer payments linked to this job.</CardDescription>
        </CardHeader>
        <CardContent>
          {paymentsQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : payments.length === 0 ? (
            <Empty className="py-8">
              <WalletCards className="mb-4 h-10 w-10 text-muted-foreground/60" />
              <p className="text-lg font-medium">No payments yet</p>
              <p className="mt-1 text-sm text-muted-foreground">Advance and invoice payments will appear here.</p>
            </Empty>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border/70">
              <table className="w-full text-sm">
                <thead className="bg-muted/35 text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Stage</th>
                    <th className="px-4 py-3">Wallet</th>
                    <th className="px-4 py-3">Reference</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id} className="border-t border-border/60">
                      <td className="px-4 py-3 text-muted-foreground">{payment.payment_date ? formatDate(payment.payment_date) : '-'}</td>
                      <td className="px-4 py-3">{getPaymentStageLabel(payment.payment_stage)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{getPaymentWalletLabel(payment)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{payment.reference || '-'}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={(payment.paid_status ?? 'paid') === 'paid' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}>
                          {payment.paid_status ?? 'paid'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums">{formatCurrency(toAmount(payment.amount))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function SummaryMetric({
  label,
  value,
  emphasized = false,
}: {
  label: string
  value: string
  emphasized?: boolean
}) {
  return (
    <div className="rounded-lg border border-border/70 px-3 py-2.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={emphasized ? 'mt-1 text-base font-semibold text-foreground' : 'mt-1 text-sm font-medium text-foreground'}>
        {value}
      </p>
    </div>
  )
}
